import { useMantineTheme } from '@mantine/core';

import {
	apiQueryKey,
	fetchApiListMemberships,
	useApiBulkCreateMemberships,
	useApiUpdateTeam,
} from '@repo/api-codegen';
import { apiListTeamsV2QueryKey } from '@repo/api-codegen/v2';
import { Text } from '@repo/foundations';
import { useQuery } from '@tanstack/react-query';
import { openConfirmModal } from '../../../components/ModalManager';
import { queryClient } from '../../index';
import { codegenApiFetchAllPages } from '../../utils/iterator';

/**
 * Hook that fetches ALL membership pages before returning
 * Uses the generic fetchAllApiPages utility
 */
function useApiListAllMemberships(teamId: string) {
	return useQuery({
		queryKey: ['memberships', 'all', teamId],
		queryFn: () =>
			codegenApiFetchAllPages(fetchApiListMemberships, {
				pathParams: { teamId },
			}),
		enabled: !!teamId,
	});
}

export function useMembershipsAndBulkCreateMemberships(teamId: string) {
	const { data: memberships } = useApiListAllMemberships(teamId);

	const {
		isPending: isBulkCreatingMemberships,
		mutateAsync: bulkCreateTeamMembership,
	} = useApiBulkCreateMemberships({
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: apiQueryKey('auth/teams/{team_id}/memberships', {
					team_id: teamId,
				}),
			});
			// Also invalidate our custom all memberships query
			queryClient.invalidateQueries({
				queryKey: ['memberships', 'all', teamId],
			});
		},
	});

	return {
		memberships,
		isBulkCreatingMemberships,
		bulkCreateTeamMembership,
	};
}

export function useTeamManagement(teamId: string) {
	const theme = useMantineTheme();
	const { mutateAsync: updateTeam, isPending: isUpdating } = useApiUpdateTeam({
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: apiQueryKey('auth/teams'),
			});
			// Also invalidate teams list endpoint (includes empty states)
			queryClient.invalidateQueries({
				queryKey: apiListTeamsV2QueryKey({}),
			});
		},
	});

	const archiveTeam = async () => {
		openConfirmModal({
			sx: {
				paddingTop: `${theme.spacing.md} !important`,
				paddingBottom: `${theme.spacing.md} !important`,
			},
			title: (
				<Text size="md" weight="bold">
					Are you sure you want to archive this team?
				</Text>
			),
			children: (
				<Text size="sm">
					Archiving this team will remove access and hide it in the sidebar for
					all team members.{' '}
				</Text>
			),
			labels: { confirm: 'Archive team', cancel: 'Cancel' },
			cancelProps: {
				size: 'md',
			},
			confirmProps: {
				size: 'md',
				variant: 'primary',
				tone: 'critical',
			},

			onConfirm: async () => {
				await updateTeam({
					pathParams: {
						teamId,
					},
					body: {
						archived: true,
					},
				});
			},
		});
	};

	const restoreTeam = async () => {
		await updateTeam({
			pathParams: {
				teamId,
			},
			body: {
				archived: false,
			},
		});
	};

	return {
		isUpdating,
		updateTeam,
		archiveTeam,
		restoreTeam,
	};
}
